// Status: active. INF-400 — machine-only org-owned mutation contracts.
import { z } from 'zod';

export type OrganizationJson =
  | null
  | boolean
  | number
  | string
  | OrganizationJson[]
  | { [key: string]: OrganizationJson };

function isJson(value: unknown, ancestors = new Set<object>()): value is OrganizationJson {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object' || ancestors.has(value)) return false;
  if (
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) !== Object.prototype &&
    Object.getPrototypeOf(value) !== null
  )
    return false;
  ancestors.add(value);
  const keys = Reflect.ownKeys(value);
  const valid = Array.isArray(value)
    ? keys.length === value.length + 1 && Array.from(value).every((item) => isJson(item, ancestors))
    : keys.every(
        (key) =>
          typeof key === 'string' && isJson((value as Record<string, unknown>)[key], ancestors),
      );
  ancestors.delete(value);
  return valid;
}

export const organizationJsonSchema = z.custom<OrganizationJson>(
  (value) => isJson(value),
  'Expected a JSON value',
);
export const organizationMutationIdSchema = z.string().min(1);
export const claimElementInputSchema = z
  .object({
    organizationId: organizationMutationIdSchema,
    elementId: organizationMutationIdSchema,
  })
  .strict();
export const merchantFaqsGetInputSchema = z
  .object({ organizationId: organizationMutationIdSchema })
  .strict();
export const merchantFaqsPutInputSchema = merchantFaqsGetInputSchema.extend({
  faqs: organizationJsonSchema,
});
export const claimElementOutputSchema = z
  .object({ outcome: z.enum(['claimed', 'already_claimed']) })
  .strict();
export const releaseElementOutputSchema = z
  .object({ outcome: z.enum(['released', 'not_owner']) })
  .strict();
export const merchantFaqsGetOutputSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('found'), faqs: organizationJsonSchema }).strict(),
  z.object({ outcome: z.literal('organization_not_found') }).strict(),
]);
export const merchantFaqsPutOutputSchema = z.object({ outcome: z.literal('updated') }).strict();
